import { UIChunkType } from '@myre/app-agent-domain';
import {
  type MetricResultBlock,
  type MetricResultCell,
} from '@myre/app-agent-domain/registry/result-block';
import { type SimpleExport } from '@myre/common-sheet';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { SheetComponent } from '@myre/ui-primitive-components';
import { CopierService } from '@myre/ui-primitive-core';

import { Component, computed, inject, input, signal } from '@angular/core';
import { isDataUIPart } from 'ai';
import { isNil } from 'lodash-es';

import { WORDINGS } from '../../../myre-wordings-loader';

import { toSheetCell } from '../../helpers/result-cell.helpers';
import { type MyreUiMessagePart } from '../../interfaces/chat.interfaces';
import { ResultHandleService } from '../../services/result-handle.service';
import { ResultTableModalService } from '../../services/result-table-modal.service';

/**
 * The card a registry answer is READ off.
 *
 * Everything engaging here arrived computed: the columns, the rows, the counts
 * and the perimeter line were built by the plan that produced them, and this
 * component only lays them out. It composes no figure and re-words no sentence —
 * the prose in the bubble beside it is the model's, the block is not.
 *
 * The perimeter sits UNDER the table on purpose. It is not a caption: it is the
 * statement of what the rows above are and are not, and a reader who has just
 * finished the table is exactly the reader who needs it.
 *
 * Both affordances go straight to the server. Opening the full table and
 * downloading the workbook are gestures on an answer the user already has;
 * replaying them as a sentence for the model to route spent a call to re-derive
 * what this card already holds.
 */
@Component({
  selector: 'app-result-block',
  standalone: true,
  imports: [SheetComponent],
  templateUrl: './result-block.component.html',
  styleUrl: './result-block.component.scss',
})
export class ResultBlockComponent {
  private readonly _copierService = inject(CopierService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);
  private readonly _resultHandleService = inject(ResultHandleService);
  private readonly _resultTableModalService = inject(ResultTableModalService);

  public readonly part = input.required<MyreUiMessagePart>();

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  protected readonly _result = computed<MetricResultBlock | null>(() => {
    const part = this.part();

    if (!isDataUIPart(part) || part.type !== UIChunkType.DataResult) {
      return null;
    }

    return part.data.result;
  });

  /** The preview as the shared sheet primitive reads it. */
  protected readonly _sheet = computed<SimpleExport | null>(() => {
    const result = this._result();

    if (result === null) {
      return null;
    }

    return {
      headers: result.columns.map(column => ({ label: column.key })),
      rows: result.previewRows.map(cells => ({
        cells: cells.map((cell, index) =>
          toSheetCell(cell, result.columns[index]),
        ),
      })),
    };
  });

  /** Transient "copied" confirmation, mirroring the message copy button. */
  protected readonly _copied = signal(false);
  private _copiedResetTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly _exporting = signal(false);
  protected readonly _exportFailed = signal(false);

  /**
   * Copy what is on screen — and, ahead of it, what it covers.
   *
   * The perimeter and the shown/total count lead the clipboard for the same
   * reason they lead the exported file: pasted rows outlive the card they came
   * from, and thirty rows out of a hundred and forty-three read as a complete
   * table the moment nothing next to them says otherwise.
   */
  protected copyRows(): void {
    const result = this._result();

    if (result === null) {
      return;
    }

    const text = [
      result.perimeterLine,
      `${result.previewRows.length} / ${result.rowCount} ${this.chatWordings.citationRows()}`,
      '',
      result.columns.map(column => column.key).join('\t'),
      ...result.previewRows.map(row =>
        row.map(cell => toClipboardCell(cell)).join('\t'),
      ),
    ].join('\n');

    if (!this._copierService.copyText(text)) {
      return;
    }

    this._copied.set(true);

    if (this._copiedResetTimer !== null) {
      clearTimeout(this._copiedResetTimer);
    }

    this._copiedResetTimer = setTimeout(() => {
      this._copied.set(false);
      this._copiedResetTimer = null;
    }, 1500);
  }

  protected openTable(): void {
    const result = this._result();

    if (result === null) {
      return;
    }

    this._resultTableModalService.open(result);
  }

  /**
   * Download the complete result. The server builds the file and names it; a
   * refusal comes back as a status, and the card says the export failed instead
   * of leaving the button looking like it worked.
   */
  protected async requestExport(): Promise<void> {
    const handleId = this._result()?.handleId;

    if (isNil(handleId) || this._exporting()) {
      return;
    }

    this._exporting.set(true);
    this._exportFailed.set(false);

    try {
      await this._resultHandleService.download({ handleId, format: 'xlsx' });
    } catch {
      this._exportFailed.set(true);
    } finally {
      this._exporting.set(false);
    }
  }
}

/** An absent value pastes as an empty column, never as a literal. */
function toClipboardCell(value: MetricResultCell): string {
  return value === null ? '' : String(value);
}
